import { Component, signal, computed, effect } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { CookieConsentComponent } from './shared/components/cookie-consent/cookie-consent.component';
import { LoginRequiredModalComponent } from './shared/components/login-required-modal/login-required-modal.component';
import { AnalyticsService } from './core/services/analytics.service';
import { ConsentService } from './core/services/consent.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FooterComponent, CookieConsentComponent, LoginRequiredModalComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  // URL als Signal (für saubere Reaktion auf Navigationen)
  url = signal<string>('');

  // abgeleitetes Signal: sind wir unter /auth?
  isAuthRoute = computed(() => this.url().startsWith('/auth'));

  constructor(private router: Router, private translate: TranslateService, private titleService: Title, private consent: ConsentService, analytics: AnalyticsService) {
    // Set default and available languages
    translate.addLangs(['de', 'en', 'es', 'it', 'fr', 'nl']);
    translate.setDefaultLang('de');
    
    // Try to use browser language or fallback to German
    const browserLang = translate.getBrowserLang();
    const savedLang = localStorage.getItem('wwhxp-language');
    
    if (savedLang && translate.getLangs().includes(savedLang)) {
      translate.use(savedLang);
    } else if (browserLang && translate.getLangs().includes(browserLang)) {
      translate.use(browserLang);
    } else {
      translate.use('de');
    }

    // Update browser title on language change
    translate.onLangChange.subscribe(() => this.updateTitle());
    this.updateTitle();

    // Initialwert setzen (wichtig für erste Render-Phase/SSR)
    this.url.set(this.router.url || '');

    // Auf Navigationsende hören und URL aktualisieren
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.url.set(this.router.url || ''));

    // Anonyme Seitenaufrufe nur nach ausdrücklicher Einwilligung erfassen (TTDSG § 25 / DSGVO).
    effect(() => {
      if (this.consent.analyticsAllowed()) {
        analytics.start();
      }
    });
  }

  private updateTitle(): void {
    const title = this.translate.instant('app.title');
    const subtitle = this.translate.instant('app.siteSubtitle');
    this.titleService.setTitle(`${title} - ${subtitle}`);
  }
}
